import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface MiniPlayerWrapperProps {
  children: React.ReactNode;
  title?: string;
  disableOnMobile?: boolean;
  dockPosition?: 'bottom-right' | 'bottom-left';
  className?: string;
}

/**
 * MiniPlayerWrapper
 * Wrap a video player to dock it as a mini-player when scrolled out of view (YouTube-style).
 */
const MiniPlayerWrapper: React.FC<MiniPlayerWrapperProps> = ({
  children,
  title,
  disableOnMobile = true,
  dockPosition = 'bottom-right',
  className,
}) => {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isDocked, setIsDocked] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [measuredHeight, setMeasuredHeight] = useState<number>(0);

  // Compute docking classes
  const dockClasses = useMemo(() => {
    const base = 'fixed z-50 shadow-xl rounded-md bg-background animate-enter';
    const pos = dockPosition === 'bottom-right' ? 'bottom-4 right-4' : 'bottom-4 left-4';
    // Width responsive; height handled by inner responsive player
    return `${base} ${pos} w-[320px] sm:w-[360px] max-w-[92vw]`;
  }, [dockPosition]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Track height to preserve layout when docking
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        if (cr.height) setMeasuredHeight(cr.height);
      }
    });
    ro.observe(containerRef.current);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    if (disableOnMobile && isMobile) return;

    const target = containerRef.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        // Dock when not visible and not dismissed
        setIsDocked(!entry.isIntersecting && !dismissed);
      },
      {
        // Make it dock once the element is fully out of view and a bit more for stability
        threshold: 0,
        rootMargin: '0px 0px -40% 0px',
      }
    );

    io.observe(target);
    return () => io.disconnect();
  }, [isMobile, disableOnMobile, dismissed]);

  // If user closed the mini player, keep original position and no docking
  const handleClose = () => setDismissed(true);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {isDocked && !dismissed && (
        <div aria-hidden style={{ height: measuredHeight || 0 }} />
      )}

      {/* Video container that moves between original and docked positions */}
      <div className={isDocked && !dismissed ? dockClasses : ''} role={isDocked && !dismissed ? "complementary" : undefined} aria-label={isDocked && !dismissed && title ? `${title} mini player` : undefined}>
        {isDocked && !dismissed && (
          <div className="relative">
            {/* Close button */}
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close mini player"
              className="absolute top-2 right-2 z-10 inline-flex items-center justify-center h-8 w-8 rounded-full bg-background/90 text-foreground shadow hover:opacity-90 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Optional title overlay for context (visually subtle) */}
            {title && (
              <div className="absolute left-3 top-2 pr-12 text-xs text-foreground/80 line-clamp-1 z-10">
                {title}
              </div>
            )}
          </div>
        )}

        {/* The video player - same element in both positions */}
        <div className="w-full">
          {children}
        </div>
      </div>
    </div>
  );
};

export default MiniPlayerWrapper;
